import {
  createTemplateRequestSchema,
  createTemplateResponseSchema,
  deleteTemplateResponseSchema,
  listTemplatesResponseSchema,
  templateInvalidResponseSchema,
  templateStartRequestSchema,
  templateStartResponseSchema,
  updateTemplateRequestSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

// Extend request objects only for frontend with necessary path params
const CRUD = {
  c: createTemplateRequestSchema,
  r: z.object({ workflowId: z.string() }),
  u: updateTemplateRequestSchema.extend({ workflowId: z.string() }),
  d: z.object({ workflowId: z.string() }),
  i: templateStartRequestSchema.extend({ workflowId: z.string() }),
};

const endpoints = {
  'templates.create': {
    method: 'POST',
    url: `${API_URL_BASE}templates`,
    requestSchema: CRUD.c,
    responseSuccessSchema: createTemplateResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: templateInvalidResponseSchema,
      403: templateInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.c,
    typeof createTemplateResponseSchema,
    {
      400: typeof templateInvalidResponseSchema;
      403: typeof templateInvalidResponseSchema;
    }
  >,
  'templates.delete': {
    method: 'DELETE',
    url: `${API_URL_BASE}templates/:workflowId`,
    requestSchema: CRUD.d,
    responseSuccessSchema: deleteTemplateResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: templateInvalidResponseSchema,
      404: templateInvalidResponseSchema,
      500: templateInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.d,
    typeof deleteTemplateResponseSchema,
    {
      403: typeof templateInvalidResponseSchema;
      404: typeof templateInvalidResponseSchema;
      500: typeof templateInvalidResponseSchema;
    }
  >,
  'templates.get': {
    method: 'GET',
    url: `${API_URL_BASE}templates/:workflowId`,
    requestSchema: CRUD.r,
    responseSuccessSchema: createTemplateResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: templateInvalidResponseSchema,
      404: templateInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.r,
    typeof createTemplateResponseSchema,
    {
      403: typeof templateInvalidResponseSchema;
      404: typeof templateInvalidResponseSchema;
    }
  >,
  'templates.get.all': {
    method: 'GET',
    url: `${API_URL_BASE}templates`,
    requestSchema: z.undefined(),
    responseSuccessSchema: listTemplatesResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof listTemplatesResponseSchema,
    z.ZodUndefined
  >,
  'templates.instantiate': {
    method: 'POST',
    url: `${API_URL_BASE}templates/:workflowId/instantiate`,
    requestSchema: CRUD.i,
    responseSuccessSchema: templateStartResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: templateInvalidResponseSchema,
      403: templateInvalidResponseSchema,
      404: templateInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.i,
    typeof templateStartResponseSchema,
    {
      400: typeof templateInvalidResponseSchema;
      403: typeof templateInvalidResponseSchema;
      404: typeof templateInvalidResponseSchema;
    }
  >,
  'templates.update': {
    method: 'PUT',
    url: `${API_URL_BASE}templates/:workflowId`,
    requestSchema: CRUD.u,
    responseSuccessSchema: createTemplateResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: templateInvalidResponseSchema,
      403: templateInvalidResponseSchema,
      404: templateInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof CRUD.u,
    typeof createTemplateResponseSchema,
    {
      400: typeof templateInvalidResponseSchema;
      403: typeof templateInvalidResponseSchema;
      404: typeof templateInvalidResponseSchema;
    }
  >,
};

export default endpoints;
